import { useEffect } from "react";
import { useSyncExternalStore } from "react";

type CacheEntry = {
  deadline: number;
  itemsKey: string;
};

const deadlineCache = new Map<string, CacheEntry>();

const listeners = new Set<() => void>();
let heartbeatInterval: ReturnType<typeof setInterval> | null = null;

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  if (!heartbeatInterval) {
    heartbeatInterval = setInterval(() => {
      listeners.forEach((l) => l());
    }, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && heartbeatInterval) {
      clearInterval(heartbeatInterval);
      heartbeatInterval = null;
    }
  };
};

const getSnapshot = () => Math.floor(Date.now() / 1000);

export const useCountdownMinutes = (
  id: string,
  seconds: number | undefined,
  itemsKey: string,
): number => {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  useEffect(() => {
    if (!itemsKey) {
      deadlineCache.delete(id);
      return;
    }

    if (!seconds) {
      return;
    }

    const existing = deadlineCache.get(id);
    if (!existing || existing.itemsKey !== itemsKey) {
      deadlineCache.set(id, {
        deadline: Date.now() + seconds * 1000,
        itemsKey,
      });
    }
  }, [id, seconds, itemsKey]);

  const entry = deadlineCache.get(id);
  if (!entry) return 0;

  const remainingMs = entry.deadline - Date.now();
  if (remainingMs <= 0) return 1;

  return Math.ceil(remainingMs / 1000 / 60);
};
